import { useState } from 'react';
import { useStore, selectCreature, selectLightOn } from '@/infra/store';
import { getCareTickService } from '@/services/care-tick';
import { useT } from '@/infra/i18n';
import { FOOD_CATALOG, type FoodId } from '@/domain/care/foods';
import { isSleeping } from '@/domain/sleep';
import { TOY_CATALOG, type ToyId } from '@/domain/care/toys';

const FONT_PIXEL = '"Courier New", monospace';
const ASSET_ROOT = '/assets/generated/house';

const FOOD_OPTIONS: Array<{ id: FoodId; color: string }> = [
  { id: 'meal', color: '#f0c870' },
  { id: 'snack', color: '#ff9ac8' },
  { id: 'fruit', color: '#7ee080' },
  { id: 'protein', color: '#ff9f62' },
];

const FOOD_GLYPHS: Record<FoodId, string[]> = {
  meal: ['..yyy..', '.yYYYy.', 'bbbbbbb', '.bbbbb.'],
  snack: ['p.....p', '.ppPpp.', '..PPP..', '.ppPpp.', 'p.....p'],
  fruit: ['...g...', '..gg...', '.rrrrr.', 'rrrrrrr', '.rrrrr.'],
  protein: ['w.....w', '.woooow', '..oooo.', 'w.oooo.', 'w.....w'],
};

const FOOD_PIXEL_COLORS: Record<string, string> = {
  y: '#9a5b24', Y: '#f0c870', b: '#4e9bd1', p: '#c53f82', P: '#ff9ac8',
  g: '#55b95a', r: '#e85a4f', w: '#ffe5bd', o: '#c96f38',
};

function FoodGlyph({ id }: { id: FoodId }) {
  return (
    <span data-testid={`food-glyph-${id}`} data-food-shape={id} style={foodGlyph}>
      {FOOD_GLYPHS[id].flatMap((row, y) => [...row].map((pixel, x) => (
        <i key={`${x}-${y}`} style={{ background: FOOD_PIXEL_COLORS[pixel] ?? 'transparent' }} />
      )))}
    </span>
  );
}

const TOY_OPTIONS: Array<{ id: ToyId; color: string }> = [
  { id: 'ball', color: '#78b7ff' }, { id: 'rope', color: '#f0c870' },
  { id: 'plush', color: '#ff9ac8' }, { id: 'puzzle', color: '#9ee68f' },
];

const TOY_GLYPHS: Record<ToyId, string[]> = {
  ball: ['..bbb..', '.bBBBb.', 'bBBwBBb', '.bBBBb.', '..bbb..'],
  rope: ['.yyyyy.', 'y.....y', '.y...y.', '..yyy..', '...y...'],
  plush: ['p.....p', '.ppppp.', 'ppwppwp', '.ppppp.', '..p.p..'],
  puzzle: ['..g.g..', '.ggggg.', 'gggGggg', '.ggggg.', '..ggg..'],
};

const TOY_PIXEL_COLORS: Record<string, string> = {
  b: '#3974a0', B: '#78b7ff', w: '#fff4d4', y: '#f0c870', p: '#ff9ac8', g: '#58a95b', G: '#baf59d',
};

function ToyGlyph({ id }: { id: ToyId }) {
  return (
    <span data-testid={`toy-glyph-${id}`} data-toy-shape={id} style={foodGlyph}>
      {TOY_GLYPHS[id].flatMap((row, y) => [...row].map((pixel, x) => (
        <i key={`${x}-${y}`} style={{ background: TOY_PIXEL_COLORS[pixel] ?? 'transparent' }} />
      )))}
    </span>
  );
}

interface BtnProps {
  icon: string;
  label: string;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
  color?: string;
  pulse?: boolean;
  title?: string;
}

function ActionBtn({ icon, label, testId, onClick, disabled, color = '#ffd6b3', pulse, title }: BtnProps) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      data-testid={testId}
      title={title}
      onClick={() => {
        if (disabled) return;
        setPressed(true);
        setTimeout(() => setPressed(false), 120);
        onClick();
      }}
      disabled={disabled}
      style={{
        background: disabled ? 'rgba(20, 20, 32, 0.78)' : pressed ? `${color}26` : 'rgba(12, 12, 24, 0.9)',
        color: disabled ? '#555566' : color,
        border: `1px solid ${disabled ? 'rgba(120, 120, 145, 0.28)' : `${color}aa`}`,
        borderRadius: 10,
        padding: '7px 8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: FONT_PIXEL,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 0.2,
        minWidth: 78,
        height: 72,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        transform: pressed ? 'translateY(2px)' : 'translate(0,0)',
        boxShadow: pressed
          ? 'inset 0 0 0 1px rgba(255,255,255,0.12)'
          : disabled
            ? 'none'
            : `0 6px 16px rgba(0,0,0,0.24), 0 0 14px ${color}22`,
        transition: 'transform 80ms steps(2), background 80ms',
        animation: pulse && !disabled ? 'btnPulsePixel 0.7s steps(2) infinite' : 'none',
        textShadow: '0 1px 0 rgba(0,0,0,0.8)',
      }}
    >
      <span style={{
        width: 38,
        height: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.42 : 1,
      }}>
        <img
          src={`${ASSET_ROOT}/${icon}`}
          alt=""
          draggable={false}
          style={{ width: 38, height: 38, objectFit: 'contain', imageRendering: 'auto' }}
        />
      </span>
      <span style={labelText}>{label}</span>
    </button>
  );
}

interface Props {
  onOpenMiniGame: () => void;
}

export function ActionButtons({ onOpenMiniGame }: Props) {
  const t = useT();
  const c = useStore(selectCreature);
  const lightOn = useStore(selectLightOn);
  const svc = getCareTickService();
  const [foodOpen, setFoodOpen] = useState(false);
  const [toyOpen, setToyOpen] = useState(false);
  if (!c) return null;
  const isEgg = c.lifecycle.stage === 'egg';
  const isDead = c.state.dead;
  const sleeping = !isEgg && !isDead && isSleeping(Date.now());
  const baseDisabled = isEgg || isDead || sleeping;
  const canHeal = c.state.sick;
  return (
    <>
      {foodOpen && !baseDisabled ? (
        <div data-testid="food-menu" style={foodMenu}>
          {FOOD_OPTIONS.map((food) => (
            <button
              key={food.id}
              data-testid={`food-${food.id}`}
              title={t(`food.${food.id}.desc`)}
              onClick={() => {
                svc.feed(food.id);
                setFoodOpen(false);
              }}
              style={{ ...foodButton, borderColor: `${food.color}cc`, color: food.color }}
            >
              <FoodGlyph id={food.id} />
              <span style={foodText}>{t(`food.${food.id}.name`)}</span>
              <span style={{ ...foodText, color: '#ffe8c8' }}>{t(`food.${food.id}.short`)}</span>
              <span style={foodStats}>
                <span style={foodStat}>F{signed(FOOD_CATALOG[food.id].hungerDelta)}</span>
                <span style={foodStat}>H{signed(FOOD_CATALOG[food.id].happinessDelta)}</span>
                <span style={foodStat}>P{signed(FOOD_CATALOG[food.id].weightDelta)}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {toyOpen && !baseDisabled ? (
        <div data-testid="toy-menu" style={foodMenu}>
          {TOY_OPTIONS.map((toy) => (
            <button
              key={toy.id}
              data-testid={`toy-${toy.id}`}
              title={t(`toy.${toy.id}.desc`)}
              onClick={() => { svc.play(toy.id); setToyOpen(false); }}
              style={{ ...foodButton, borderColor: `${toy.color}cc`, color: toy.color }}
            >
              <ToyGlyph id={toy.id} />
              <span style={foodText}>{t(`toy.${toy.id}.name`)}</span>
              <span style={{ ...foodText, color: '#ffe8c8' }}>{t(`toy.${toy.id}.short`)}</span>
              <span style={foodStats}>
                <span style={foodStat}>H{signed(TOY_CATALOG[toy.id].happinessDelta)}</span>
                <span style={foodStat}>P{signed(TOY_CATALOG[toy.id].weightDelta)}</span>
                <span style={foodStat}>D{signed(TOY_CATALOG[toy.id].disciplineDelta)}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <div data-testid="action-buttons" style={row}>
        <ActionBtn
          testId="btn-feed"
          icon="icon-feed.png"
          label={t('action.feed')}
          disabled={baseDisabled}
          color="#f0c870"
          onClick={() => { setToyOpen(false); setFoodOpen((open) => !open); }}
        />
        <ActionBtn testId="btn-play" icon="icon-play.png" label={t('action.play')} disabled={baseDisabled} color="#ff6eb4" onClick={() => { setFoodOpen(false); setToyOpen((open) => !open); }} />
        <ActionBtn testId="btn-clean" icon="icon-clean.png" label={t('action.clean')} disabled={isDead || sleeping || isEgg} color="#6cf08f" onClick={() => svc.clean()} />
        <ActionBtn testId="btn-discipline" icon="icon-discipline.png" label={t('action.discipline')} disabled={isDead || isEgg || sleeping} color="#ff8844" pulse={c.state.tantrum} onClick={() => svc.discipline()} />
        <ActionBtn testId="btn-heal" icon="icon-heal.png" label={t('action.heal')} disabled={isDead || sleeping || !canHeal} color="#44ddaa" pulse={canHeal} onClick={() => svc.heal()} />
        <ActionBtn testId="btn-minigame" icon="icon-minigame.png" label={t('action.minigame')} disabled={baseDisabled} color="#b388ff" onClick={onOpenMiniGame} />
        <ActionBtn testId="btn-light" icon={lightOn ? 'icon-light.png' : 'icon-light-off.png'} label={t('action.light')} color={lightOn ? '#f0c870' : '#888899'} onClick={() => svc.toggleLight()} />
        <style>{`
          @keyframes btnPulsePixel {
            0%,100% { box-shadow: 0 4px 12px rgba(255,136,68,0.3); }
            50% { box-shadow: 0 4px 18px rgba(255,136,68,0.8); }
          }
        `}</style>
      </div>
    </>
  );
}

function signed(value: number): string {
  if (value > 0) return `+${value}`;
  return `${value}`;
}

const row: React.CSSProperties = {
  position: 'fixed',
  bottom: 14,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  zIndex: 90,
  flexWrap: 'wrap',
  justifyContent: 'center',
  maxWidth: 'min(660px, calc(100vw - 20px))',
  padding: 8,
  background: 'rgba(8, 10, 22, 0.76)',
  border: '1px solid rgba(255, 226, 188, 0.4)',
  borderRadius: 12,
  boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
  backdropFilter: 'blur(4px)',
};

const labelText: React.CSSProperties = {
  display: 'block',
  maxWidth: 68,
  lineHeight: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const foodMenu: React.CSSProperties = {
  position: 'fixed',
  bottom: 104,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 95,
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(84px, 1fr))',
  gap: 8,
  maxWidth: 'min(460px, calc(100vw - 20px))',
  padding: 8,
  background: 'rgba(8, 10, 22, 0.84)',
  border: '1px solid rgba(255, 226, 188, 0.44)',
  borderRadius: 12,
  boxShadow: '0 12px 30px rgba(0,0,0,0.28)',
  backdropFilter: 'blur(4px)',
};

const foodButton: React.CSSProperties = {
  minHeight: 96,
  border: '1px solid',
  borderRadius: 10,
  background: 'rgba(12, 12, 24, 0.92)',
  cursor: 'pointer',
  fontFamily: FONT_PIXEL,
  fontSize: 10,
  textTransform: 'uppercase',
  textShadow: '0 1px 0 rgba(0,0,0,0.8)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
};

const foodGlyph: React.CSSProperties = {
  width: 35,
  height: 25,
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 5px)',
  gridAutoRows: 5,
  alignContent: 'center',
  imageRendering: 'pixelated',
};

const foodText: React.CSSProperties = {
  display: 'block',
  maxWidth: 76,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const foodStats: React.CSSProperties = {
  display: 'flex',
  gap: 3,
  marginTop: 2,
};

const foodStat: React.CSSProperties = {
  minWidth: 22,
  padding: '1px 3px',
  borderRadius: 4,
  background: 'rgba(255,255,255,0.1)',
  color: '#f8f0d8',
  fontSize: 9,
  lineHeight: '12px',
};
